import { useState, type JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useFailClosedFlag } from '@/hooks/useFailClosedFlag';
import { trackEvent } from '@/lib/analytics';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { cn } from '@/lib/utils';
import { HELP_BASE_PATH } from './docsManifest';
import { FullDocsDialog } from './FullDocsDialog';
import { HelpPopover } from './HelpPopover';
import { resolveDocKey } from './resolveDocKey';
import { useDocsManifest } from './useDocsManifest';

export const HELP_EVENTS = {
  POPOVER_OPENED: 'help_popover_opened',
  FULL_DOCS_OPENED: 'full_docs_opened',
} as const;

interface HelpButtonProps {
  /** The page's title, shown by the help for a screen without a doc. */
  screenTitle?: string;
}

/**
 * The header Help button (behind `in_app_docs`). Opens the current screen's
 * help; from there, the full docs in a dialog. A screen without a doc still
 * gets the button and, while the user can see any doc, a way into the full
 * docs.
 */
export function HelpButton({
  screenTitle,
}: HelpButtonProps): JSX.Element | null {
  const { enabled } = useFailClosedFlag(flagsmithFeatureFlags.IN_APP_DOCS);
  const { pathname, search } = useLocation();
  const manifest = useDocsManifest(enabled);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [fullDocsPath, setFullDocsPath] = useState<string | null>(null);

  if (!enabled) return null;

  const screenKey = resolveDocKey(pathname, search);
  const doc = manifest.docForScreen(screenKey);

  const onPopoverOpenChange = (open: boolean): void => {
    setPopoverOpen(open);
    if (open) {
      trackEvent(HELP_EVENTS.POPOVER_OPENED, {
        screen_key: screenKey,
        doc_key: doc?.key ?? null,
      });
    }
  };

  const openFullDocs = (): void => {
    // A screen without a doc opens the docs at their first page.
    const path = doc?.path ?? manifest.docs[0]?.path ?? HELP_BASE_PATH;
    setPopoverOpen(false);
    setFullDocsPath(path);
    trackEvent(HELP_EVENTS.FULL_DOCS_OPENED, {
      screen_key: screenKey,
      doc_key: doc?.key ?? null,
    });
  };

  const active = popoverOpen || fullDocsPath !== null;

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Help"
            data-active={active ? 'true' : undefined}
            className={cn(
              'text-base font-semibold',
              active &&
                'border-violet-400 text-violet-600 ring-2 ring-violet-200 hover:text-violet-700 dark:border-violet-400 dark:text-violet-300 dark:ring-violet-500/30 dark:hover:text-violet-200'
            )}
          >
            ?
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[22rem] p-0">
          <HelpPopover
            doc={doc}
            screenTitle={screenTitle}
            isLoading={manifest.isLoading}
            isError={manifest.isError}
            canOpenFullDocs={manifest.isLoading || manifest.docs.length > 0}
            onOpenFullDocs={openFullDocs}
            onClose={() => setPopoverOpen(false)}
          />
        </PopoverContent>
      </Popover>
      <FullDocsDialog
        open={fullDocsPath !== null}
        onOpenChange={open => {
          if (!open) setFullDocsPath(null);
        }}
        initialPath={fullDocsPath ?? HELP_BASE_PATH}
        screenDoc={doc}
        manifest={manifest}
      />
    </>
  );
}
